import React, { useCallback, useEffect, useState } from 'react'

import { rnNavigation } from 'app/utils/appConfig'

import SectionLayout from '../../components/reusable/SectionLayout'
import { ROUTES as FARMER_360_ROUTES } from '../../routes/types'
import FarmerProductCard from './FarmerProductCard'
import { FarmerProducts } from '../../models/farmer'
import fetchFarmerProducts from '../../apis/fetchFarmerProducts'
import AddProductsButton from './AddProductsBtn'
import SelectCropModal from '../crop-calendar/SelectCropModal'
import { Crop } from '../../models/crop'
import fetchFarmerCrops from '../../apis/fetchFarmerCrops'
import CropSelectionComponent from '../crops/AddCropModal'
import AddFarmerProductsModal from './AddFarmerProductsModal'

interface Props {
  farmerId: string
  reload: () => void
  isEditAllowed: boolean
  highlight: boolean
}

const FarmerProductsSection = ({
  farmerId,
  isEditAllowed,
  reload,
  highlight,
}: Props) => {
  const [products, setProducts] = useState<FarmerProducts[]>([])
  const [loading, setLoading] = useState(true)
  const [crops, setCrops] = useState<Crop[]>([])
  const [selectedCrop, setSelectedCrop] = useState<Crop>()
  const [showAddNewCropModal, setShowAddNewCropModal] = useState(false)
  const [addFarmerProductModalVisibilty, setAddFarmerProductsModalVisibility] =
    useState(false)

  const [isModalVisible, setModalVisibility] = useState(false)

  const fetchAndSetFarmerCrops = async () => {
    setLoading(true)
    const results = await fetchFarmerCrops(farmerId)
    if (results) {
      setCrops(results)
    }
    setLoading(false)
  }

  const fetchFarmerProductsAndSet = useCallback(async () => {
    const products = await fetchFarmerProducts(farmerId)
    if (products) setProducts(products)
  }, [farmerId])

  const handleCropSelection = (selectedCrop: Crop) => {
    setSelectedCrop(selectedCrop)
    setAddFarmerProductsModalVisibility(true)
    setModalVisibility(false)
  }

  useEffect(() => {
    if (isEditAllowed) fetchAndSetFarmerCrops()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditAllowed])

  useEffect(() => {
    fetchFarmerProductsAndSet().finally(() => setLoading(false))
  }, [fetchFarmerProductsAndSet])

  return (
    <SectionLayout
      title="Products used by farmer"
      description={''}
      loading={loading}
      onViewAllClick={() =>
        rnNavigation({
          name: FARMER_360_ROUTES.FARMER_PRODUCTS_LISTING,
          params: { farmerId, isEditAllowed },
        })
      }
      isViewAllCTADisabled={products.length === 0}
      highlight={highlight}>
      {products.map(({ crop, products }, index) => (
        <FarmerProductCard
          key={index}
          crop={crop}
          products={products}
          handleCTAClick={() =>
            rnNavigation({
              name: FARMER_360_ROUTES.FARMER_PRODUCTS_LISTING,
              params: { farmerId, isEditAllowed },
            })
          }
        />
      ))}
      {isEditAllowed && (
        <AddProductsButton
          onClick={() => setModalVisibility(true)}
          style={{ marginHorizontal: 80 }}
        />
      )}

      <SelectCropModal
        heading="Select a crop"
        description="to add input products used on crops"
        isVisible={isModalVisible}
        handleClose={() => setModalVisibility(false)}
        crops={crops}
        onCropSelect={selectedCrop => handleCropSelection(selectedCrop)}
        onAddNewCropClick={() => setShowAddNewCropModal(true)}
      />

      {selectedCrop && (
        <AddFarmerProductsModal
          isVisible={addFarmerProductModalVisibilty}
          farmerId={farmerId}
          crop={selectedCrop!}
          onClose={() => {
            setAddFarmerProductsModalVisibility(false)
            setSelectedCrop(undefined)
          }}
          onSubmit={() => {
            setAddFarmerProductsModalVisibility(false)
            setSelectedCrop(undefined)
            reload()
          }}
          mappedProducts={
            products.find(p => p.crop.id === selectedCrop.id)?.products || []
          }
        />
      )}

      {showAddNewCropModal && (
        <CropSelectionComponent
          farmerId={farmerId}
          userCrops={crops}
          onClose={() => setShowAddNewCropModal(false)}
          onSubmit={reload}
        />
      )}
    </SectionLayout>
  )
}

export default FarmerProductsSection
